import React, { useState, useMemo, useRef, useEffect } from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import {
  fetchFlights,
  getFlightNumberHistory,
  getFlightNumbers,
  getLastFlightNumber,
} from "apis/loadsServices";
import SearchResults from "./SearchResults";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import HeroBg from "../../assets/images/top-main2.gif";
import { formatDateTime } from "utils/dateUtils";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
`;

interface SearchFlightsNumberProps {
  onShowSearchResults?: (show: boolean) => void;
  onPostRequestSuccess?: () => void;
  tabButtons?: React.ReactNode;
  onBack?: () => void;
  readOnly?: boolean;
  breadcrumbLabel?: string;
}

// localStorage utility functions for last flight search
const LAST_FLIGHT_SEARCH_STORAGE_KEY = "staffTraveler_lastFlightSearch";

interface LastFlightSearchData {
  flightNumber: string;
  dates: string[]; // Array of "YYYY-MM-DD" formatted dates
  savedAt: number; // Timestamp
}

const saveLastFlightSearchToStorage = (flightNumber: string, dates: string[]): void => {
  try {
    const data: LastFlightSearchData = {
      flightNumber,
      dates,
      savedAt: Date.now(),
    };
    localStorage.setItem(LAST_FLIGHT_SEARCH_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving last flight search to localStorage:", error);
  }
};

const loadLastFlightSearchFromStorage = (): LastFlightSearchData | null => {
  try {
    const stored = localStorage.getItem(LAST_FLIGHT_SEARCH_STORAGE_KEY);
    if (!stored) return null;
    
    const data: LastFlightSearchData = JSON.parse(stored);
    // Validate data structure
    if (data.flightNumber && Array.isArray(data.dates)) {
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error loading last flight search from localStorage:", error);
    return null;
  }
};


const SearchFlightsNumber: React.FC<SearchFlightsNumberProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
  tabButtons,
  onBack,
  readOnly = false,
  breadcrumbLabel = "Loads",
}) => {
  const { showToast } = useToastStore.getState();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>("");
  const [flightNumberOptions, setFlightNumberOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [date, setDate] = useState<string>(
    new Date(Date.now() + 86400000).toISOString().split("T")[0]
  ); // Default to Tomorrow's date
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<ConnectionFlight[]>([]);
  // const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer">("none");
  const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | "one" | "two" | "three" | null>("none");
  const [isLoadingFlights, setIsLoadingFlights] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    flightNumber?: string;
    date?: string;
    apiResponse?: string;
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Track if we're auto-resetting to prevent infinite loops
  const isAutoResetting = useRef<boolean>(false);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [startDate, endDate] = range;
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const flightsPerPage = 9;
  const totalPages = Math.ceil(totalFlights / flightsPerPage);

  const handleFlightNumberChange = (selectedOption: any) => {
    if (selectedOption) {
      setFlightNumber(selectedOption.value);
      setErrors((prev) => ({
        ...prev,
        flightNumber: undefined,
        apiResponse: undefined,
      }));
      
      // Auto-open the date picker when flight number is selected
      setTimeout(() => {
        setIsCalendarOpen(true);
      }, 300);
    } else {
      setFlightNumber("");
      setInputValue("");
    }
  };

  // Debounce input value with 800ms delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  // Load flight number options when debounced input value changes
  useEffect(() => {
    if (debouncedInputValue !== undefined && debouncedInputValue.trim() !== "") {
      loadFlightNumberOptions(debouncedInputValue);
    }
  }, [debouncedInputValue]);

  const loadFlightNumberOptions = async (inputValue: string) => {
    setIsLoading(true);
    try {
      const response = await getFlightNumbers(inputValue);

      // Safely access response data with proper error handling
      let options: Array<{ value: string; label: string }> = [];

      // First, check if response exists and has data property
      if (response && Array.isArray(response.data)) {
        options = response.data
          .filter(
            (item: any): item is { flightNumber: string } =>
              item &&
              typeof item === "object" &&
              typeof item.flightNumber === "string"
          )
          .map((item: { flightNumber: string }) => ({
            value: item.flightNumber,
            label: item.flightNumber,
          }));
      }
      // If response.data isn't an array but response itself is the array
      else if (response && Array.isArray(response)) {
        options = response
          .filter(
            (item: any): item is { flightNumber: string } =>
              item &&
              typeof item === "object" &&
              typeof item.flightNumber === "string"
          )
          .map((item: { flightNumber: string }) => ({
            value: item.flightNumber,
            label: item.flightNumber,
          }));
      }
      // If we got a different response structure that includes the array elsewhere
      else if (response && typeof response === "object") {
        // Try to find an array of flight numbers somewhere in the response
        const responseValues = Object.values(response);
        for (const value of responseValues) {
          if (Array.isArray(value)) {
            const validItems = value.filter(
              (item: any): item is { flightNumber: string } =>
                item &&
                typeof item === "object" &&
                typeof item.flightNumber === "string"
            );

            options = validItems.map((item: { flightNumber: string }) => ({
              value: item.flightNumber,
              label: item.flightNumber,
            }));
            break;
          }
        }
      }

      setFlightNumberOptions(options);
      return options;
    } catch (error) {
      console.error("Error fetching flight number options:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | null) => {
  const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null) => {
    if (!filter) return;
    setStopsFilter(filter);
    // If we have already searched, trigger a new search with the updated filter
    if (hasSearched) {
      getFlights({ stopsFilter: filter });
    }
  };

  const handleLastSearchClick = async () => {
    try {
      // First check localStorage
      let lastSearchData: LastFlightSearchData | null = loadLastFlightSearchFromStorage();
      
      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const { lastSearchFlightNumber } = await getLastFlightNumber();
          if (lastSearchFlightNumber) {
            // Convert UTC date to YYYY-MM-DD format
            const lastSearchDate = new Date(lastSearchFlightNumber.date)
              .toISOString()
              .split("T")[0];
            
            lastSearchData = {
              flightNumber: lastSearchFlightNumber.flightNumber,
              dates: [lastSearchDate],
              savedAt: Date.now(),
            };
            
            // Save to localStorage for future use
            saveLastFlightSearchToStorage(
              lastSearchFlightNumber.flightNumber,
              [lastSearchDate]
            );
          } else {
            console.log("No last search data available");
            hideLoader();
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          return;
        } finally {
          hideLoader();
        }
      }
      
      // Populate fields with stored data
      if (lastSearchData) {
        const { flightNumber, dates } = lastSearchData;
        
        setFlightNumber(flightNumber);
        
        // Convert date strings to Date objects for calendar
        const dateObjects = dates.map(dateStr => new Date(dateStr));
        setSelectedDates(dateObjects);
        setDatesInRange(dates);
        
        setErrors({}); // Clear any existing errors

        // Trigger search
        setHasSearched(true);
        onShowSearchResults?.(true);
        setPageNumber(1);
        
        try {
          showLoader();
          await getFlights({ flightNumber, dates, pageNumber: 1 });
          
          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement = document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for last search:", error);
        } finally {
          hideLoader();
        }
      }
    } catch (error) {
      console.error("Failed to load last search:", error);
    }
  };

  // Fetch flights when page number changes (but not on initial load)
  useEffect(() => {
    if (hasSearched && PageNumber > 1) {
      const fetchFlightsForPage = async () => {
        try {
          showLoader();
          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        } finally {
          hideLoader();
        }
      };
      fetchFlightsForPage();
    }
  }, [PageNumber]);

  // Auto-reset filter to "none" when "one-or-fewer" returns no valid connection flights
  useEffect(() => {
    if (
      hasSearched &&
      stopsFilter === "one-or-fewer" &&
      !isLoadingFlights &&
      !isAutoResetting.current
    ) {
      // Check if there are any valid connection flights (must have at least 2 segments)
      const hasValidConnections = connectionFlights.some(
        (connection) => connection.segments && connection.segments.length >= 2
      );
      
      // If no valid connection flights and no direct flights, revert to non-stop
      if (!hasValidConnections && flights.length === 0) {
        // Prevent infinite loops by setting the flag
        isAutoResetting.current = true;
        // Reset filter to "none" and trigger a new search
        setStopsFilter("none");
        // Reset the flag after a short delay to allow the filter change to process
        setTimeout(() => {
          isAutoResetting.current = false;
        }, 100);
      }
    }
  }, [flights.length, connectionFlights, stopsFilter, hasSearched, isLoadingFlights]);

  const getFlights = async (overrideParams?: {
    // stopsFilter?: "none" | "one-or-fewer";
    stopsFilter?: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    flightNumber?: string;
    dates?: string[];
    pageNumber?: number;
  }) => {
    setIsLoadingFlights(true);
    try {
      const currentFilter = overrideParams?.stopsFilter ?? stopsFilter;
      let Stops: number | undefined;
      let IncludeDirect: boolean | undefined;
      let IncludeConnections: boolean | undefined;

      if (currentFilter === "none") {
        IncludeDirect = true;
        IncludeConnections = false;
        Stops = 0;
      } else if (currentFilter === "one-or-fewer") {
        IncludeDirect = false;
        IncludeConnections = true;
      } else if (currentFilter === "one") {
        Stops = 1;
      } else if (currentFilter === "two") {
        Stops = 2;
      } else if (currentFilter === "three") {
        Stops = 3;
      } else {
        // Default: non-stop only
        IncludeDirect = true;
        IncludeConnections = false;
      }

      const params = {
        FlightNumber: overrideParams?.flightNumber ?? flightNumber,
        Dates: overrideParams?.dates ?? datesInRange,
        PageNumber: overrideParams?.pageNumber ?? PageNumber,
        PageSize: flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
      };
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections || []) as ConnectionFlight[];

      // Check if we requested connections but got direct flights instead
      // This happens when API returns direct flights in items array when IncludeConnections: true
      if (currentFilter === "one-or-fewer" && connectionsData.length === 0 && rawFlightData.length > 0) {
        // Check if rawFlightData contains direct flights (stops: 0 or intermediateAirports: [])
        // The API response has 'stops' field in the raw data
        const hasDirectFlights = rawFlightData.some(
          (flight: any) => {
            return (flight.stops === 0 || flight.stops === undefined) ||
                   (flight.intermediateAirports && Array.isArray(flight.intermediateAirports) && flight.intermediateAirports.length === 0);
          }
        );
        
        // If we got direct flights instead of connections, we need to revert to non-stop
        if (hasDirectFlights && !isAutoResetting.current) {
          isAutoResetting.current = true;
          setStopsFilter("none");
          // Don't process this response, let the filter change trigger a new search
          setIsLoadingFlights(false);
          setTimeout(() => {
            isAutoResetting.current = false;
          }, 100);
          return;
        }
      }
      
      const flightData = rawFlightData as FlightDetails[];

      // Generate composite IDs for direct flights
      const flightsWithCompositeIds = flightData.map((flight) => {
        const baseId = flight.id ?? `${Date.now()}-${Math.random()}`;
        const scheduleDate = flight.scheduleDate || flight.date;
        const compositeId = scheduleDate
          ? `${baseId}_${scheduleDate.replace(/[^0-9]/g, "")}`
          : String(baseId);

        return {
          ...flight,
          compositeId,
        };
      });

      // Generate composite IDs for connection flights
      const connectionsWithCompositeIds = connectionsData.map((connection) => {
        // Generate composite IDs for each segment
        const segmentsWithIds = connection.segments.map((segment) => {
          const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
          const departureTime = segment.departureTime;
          const compositeId = departureTime
            ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}`
            : String(baseId);

          return {
            ...segment,
            compositeId,
          };
        });

        // Generate composite ID for the connection itself
        // Use the first segment's composite ID as part of the connection ID
        const firstSegmentId = segmentsWithIds[0]?.compositeId || "";
        const connectionCompositeId = `conn_${firstSegmentId}_${connection.totalDuration}`;

        return {
          ...connection,
          segments: segmentsWithIds,
          compositeId: connectionCompositeId,
        };
      });

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);

      // If API doesn't provide totalCount, estimate it based on current page and flight count
      if (response.data.total !== undefined) {
        setTotalFlights(response.data.total);
      } else {
        // Fallback: if we get less than flightsPerPage, we're on the last page
        const estimatedTotal =
          flightData.length < flightsPerPage
            ? (PageNumber - 1) * flightsPerPage + flightData.length
            : PageNumber * flightsPerPage; // This is a minimum estimate
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      showToast(apiMessage || "Failed to fetch flights.", "error", 3000);
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  const handleSearchClick = async () => {
    setHasSearched(true);
    onShowSearchResults?.(true);
    setPageNumber(1); // Reset to first page on new search
    const newErrors: typeof errors = {};
    if (!flightNumber) {
      newErrors.flightNumber = "*Flight number is required";
    }
    // if (!date) newErrors.date = "*Date is required";
    // setErrors(newErrors);

    if (!datesInRange) newErrors.date = "*Please select a date range.";

    if (Object.keys(newErrors).length === 0) {
      try {
        showLoader();
        await getFlights();
        
        // Save to localStorage after successful search
        saveLastFlightSearchToStorage(flightNumber, datesInRange);

        // Scroll to search results after a brief delay to ensure content is rendered
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching flights:", error);
      } finally {
        hideLoader();
      }
    }
  };

  const handleClearSearch = () => {
    setFlightNumber("");
    setInputValue("");
    setDebouncedInputValue("");
    setSelectedDates([]);
    setDatesInRange([]);
    setRange([null, null]);
    setFlights([]);
    setConnectionFlights([]);
    setStopsFilter("none");
    setErrors({});
    setHasSearched(false);
    setPageNumber(1);
    setTotalFlights(0);
    onShowSearchResults?.(false);
  };

  const getDatesBetween = (start: Date, end: Date) => {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleChange = (update: [Date | null, Date | null]) => {
    setRange(update);
    if (update[0] && update[1]) {
      setDatesInRange(getDatesBetween(update[0], update[1]));
    } else {
      setDatesInRange([]);
    }
  };

  const formatDateLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime()
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = () => {
    setDatesInRange(selectedDates.map((d) => formatDateLocal(d)));
    setIsCalendarOpen(false);
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    // if (!isLoadingFlights && flights.length === 0 && connectionFlights.length === 0) {
    //   return (
    //     <p className="text-center text-gray-500 text-lg mt-6">No Data Found</p>
    //   );
    // }
    // onShowSearchResults?.(true);
    return (
      <div id="search-results">
        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          isLoadingFlights={isLoadingFlights}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={onPostRequestSuccess}
          sourceLabel={flightNumber}
          totalResults={totalFlights}
          hideConnectedFilter={true}
          hideInternalFilters={true}
          readOnly={readOnly}
        />
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="inline-flex items-center bg-[#1F2937] rounded-full px-1 py-1 gap-1">
              <button
                onClick={() => { if (PageNumber > 1) setPageNumber(PageNumber - 1); }}
                disabled={PageNumber === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-white text-sm font-medium px-3">
                {PageNumber} / {totalPages}
              </span>
              <button
                onClick={() => { if (PageNumber < totalPages) setPageNumber(PageNumber + 1); }}
                disabled={PageNumber === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    isLoadingFlights,
    onShowSearchResults,
    PageNumber,
    totalPages,
    totalFlights,
    date,
    datesInRange,
    onPostRequestSuccess,
  ]);

  // Load initial flight number options
  // useEffect(() => {
  //   // Try to load initial options
  //   try {
  //     loadFlightNumberOptions("");
  //   } catch (err) {
  //     console.error("Failed to load initial flight numbers:", err);
  //   }
  // }, []);

  const filterSidebar = (
    <div
      className={`w-[220px] flex-shrink-0 ${hasSearched ? "hidden md:block" : "hidden"}`}
    >
      <div className="sticky top-4">
        {/* Breadcrumb — outside card */}
        <div className="flex items-center gap-3 mb-4 whitespace-nowrap">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors flex-shrink-0"
            onClick={() => {
              handleClearSearch();
              onBack?.();
            }}
          >
            <svg
              className="w-4 h-4 text-[#1F2937]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
          <span className="text-gray-400 text-sm">&gt;</span>
          <span className="text-[#1F2937] text-sm font-medium">Flight No.</span>
        </div>

        {/* Filter card */}
        <div className="bg-white rounded-2xl shadow-sm p-5 min-h-[calc(100vh-200px)]">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[#1F2937] text-base font-bold">Filters</p>
            <button
              type="button"
              className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
              title="Reset filters"
              onClick={() => handleStopsFilterChange("none")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <p className="text-[#9CA3AF] text-xs mb-4">
            Showing {totalFlights || 0} results
          </p>
          <p className="text-[#1F2937] text-sm font-semibold mb-3">Stops</p>
          <div className="flex flex-col gap-3">
            {(["none", "one", "two", "three"] as const).map((val) => {
              const labels: Record<string, string> = {
                none: "Non-stop",
                one: "1 Stop",
                two: "2 Stops",
                three: "3 Stops",
              };
              return (
                <label
                  key={val}
                  className="flex items-center cursor-pointer group"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="stops-flight"
                      checked={stopsFilter === val}
                      onChange={() => handleStopsFilterChange(val)}
                      className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                    />
                    <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                      {labels[val]}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-0">
      <style>{datePickerStyles}</style>

      <div className="flex gap-4 pt-4">
        {filterSidebar}
        <div className="flex-1 min-w-0">
          {/* Tabs + Form wrapped in gif bg */}
          <div className={`relative ${hasSearched ? "rounded-2xl mt-4 pb-3" : ""}`}>
        {hasSearched && (
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${HeroBg})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
          </div>
        )}
        <div className={`relative z-10 ${hasSearched ? "px-3 pt-3" : ""}`}>
          {tabButtons}
        </div>
        <div className={`relative z-20 bg-white shadow-xl p-3 overflow-visible ${hasSearched ? "mx-3 mb-3 rounded-2xl rounded-tl-none" : "rounded-2xl rounded-tl-none"}`}>
        <div className="flex items-center justify-between pb-3">
          <p className="text-[#1F2937] text-sm font-medium">
            Search by Flight Number
          </p>
          <div className="flex items-center gap-3">
            <button
              className="flex items-center gap-1 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
              onClick={handleClearSearch}
              type="button"
            >
              <i className="st-close text-xs"></i>
              <span className="text-xs">Clear</span>
            </button>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFFBEB] border border-[#FFBC00] rounded-lg hover:bg-[#FEF3C7] transition-colors"
              onClick={handleLastSearchClick}
              type="button"
            >
              <i className="st-previous text-[#FFBC00] text-xs" />
              <span className="font-semibold text-[#FFBC00] text-xs">
                Last Search
              </span>
            </button>
          </div>
        </div>
        {/* Fields in separate bordered containers */}
        <div className="flex items-stretch gap-4 mb-3 overflow-visible">
          {/* Flight No. field */}
          <div className="relative flex-[2] p-3 border border-[#E5E7EB] rounded-xl">
            <label className="flex items-center gap-1.5 mb-1 text-xs text-[#9CA3AF]">
              <i className="st-flight text-[#9CA3AF] text-[11px]"></i>
              Flight No
            </label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              value={
                flightNumber
                  ? { value: flightNumber, label: flightNumber }
                  : null
              }
              isLoading={isLoading}
              isClearable={true}
              isSearchable={true}
              name="flightNumber"
              options={flightNumberOptions}
              onChange={handleFlightNumberChange}
              onInputChange={(inputValue) => {
                setInputValue(inputValue);
              }}
              onFocus={() => {
                if (flightNumberOptions.length === 0) {
                  loadFlightNumberOptions("");
                }
              }}
              placeholder="Select or search flight number"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: "transparent",
                  border: "none",
                  padding: "0",
                  minHeight: "32px",
                  boxShadow: "none",
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0",
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                  margin: "0",
                  padding: "0",
                }),
                indicatorSeparator: () => ({
                  display: "none",
                }),
                dropdownIndicator: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0 4px",
                }),
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: "#9CA3AF",
                  fontSize: "14px",
                }),
                singleValue: (baseStyles) => ({
                  ...baseStyles,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1F2937",
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isSelected
                    ? "#2563EB"
                    : state.isFocused
                      ? "#DBEAFE"
                      : undefined,
                  color: state.isSelected ? "white" : undefined,
                }),
                menuPortal: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 9999,
                }),
              }}
            />
            {errors.flightNumber && (
              <p className="text-red-500 text-[11px] mt-0.5">{errors.flightNumber}</p>
            )}
          </div>

          {/* Date field */}
          <div className="relative flex-1 p-3 border border-[#E5E7EB] rounded-xl">
            <label className="flex items-center gap-1.5 mb-1 text-xs text-[#9CA3AF]">
              <i className="st-calendar text-[#9CA3AF] text-[11px]"></i>
              Date
            </label>
            <div className="relative">
              <DatePicker
                selected={null}
                onChange={handleDateChange}
                onCalendarClose={() => setIsCalendarOpen(false)}
                onCalendarOpen={() => setIsCalendarOpen(true)}
                open={isCalendarOpen}
                onClickOutside={() => setIsCalendarOpen(false)}
                shouldCloseOnSelect={false}
                minDate={new Date()}
                placeholderText={`${datesInRange.length > 0 ? `Selected Dates (${datesInRange.length})` : "Select travel dates"}`}
                className={`block w-full py-1.5 border-none bg-transparent text-sm text-[#1F2937] font-medium ${datesInRange.length > 0 ? "placeholder-black" : "placeholder-gray-400"} focus:outline-none cursor-pointer`}
                highlightDates={selectedDates}
                inline={false}
                wrapperClassName="w-full"
                onInputClick={() => setIsCalendarOpen(true)}
              >
                <div className="flex justify-between w-full px-2 pt-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      clearAllDates();
                    }}
                    className="px-4 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
                    type="button"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleSaveDates();
                    }}
                    className="px-4 py-2 text-sm text-white bg-[#FFBC00] rounded-lg hover:bg-[#e6a800]"
                    type="button"
                  >
                    Save ({selectedDates.length})
                  </button>
                </div>
              </DatePicker>
              {datesInRange.length > 0 && (
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-1 text-lg cursor-pointer text-gray-400 hover:text-blue-600 rounded-e-md focus:outline-none"
                  type="button"
                  title="Edit dates"
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              )}
            </div>
            {errors.date && (
              <p className="text-red-500 text-[11px] mt-0.5">{errors.date}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end">
        <button
          className="p-3.5 rounded-xl text-white text-center bg-[#FBBF24] hover:bg-[#F59E0B] text-sm font-semibold tracking-wide flex gap-2 transition-colors"
          onClick={handleSearchClick}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Find Your Flight
        </button>
        </div>
        </div>
      </div>
      {searchResults}
        </div>
      </div>
    </div>
  );
};

export default SearchFlightsNumber;
